import { ArrowLeft, Phone, Mail, CalendarDays, Ticket, StickyNote } from "lucide-react";
import { buildCustomers } from "@/components/admin/CustomersList";

export function CustomerProfile({ email, bookings, tickets, onBack }) {
  const c = buildCustomers(bookings, tickets).find((x) => x.email === email);
  if (!c) return <div className="text-slate-500 text-sm">Customer not found. <button type="button" onClick={onBack} className="dg-link">Back</button></div>;
  const sessions = [...c.bookings].sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at));
  const tix = [...c.tickets].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const notes = sessions.filter((b) => b.admin_notes);

  return (
    <div className="space-y-6" data-testid="customer-profile">
      <button type="button" onClick={onBack} className="dg-btn-ghost rounded-full px-4 py-1.5 text-xs flex items-center gap-1.5" data-testid="customer-back"><ArrowLeft className="w-3 h-3" /> All customers</button>
      <div className="dg-card rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="dg-eyebrow">Customer</div>
            <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold font-display" data-testid="customer-name">{c.name || c.email}</h2>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              <a href={`mailto:${c.email}`} className="dg-link flex items-center gap-1.5"><Mail className="w-4 h-4" /> {c.email}</a>
              {c.phone && <a href={`tel:${c.phone}`} className="dg-link flex items-center gap-1.5"><Phone className="w-4 h-4" /> {c.phone}</a>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[["Sessions", c.sessions], ["Paid", `$${c.paid.toFixed(2)}`], ["Tickets", c.tickets.length]].map(([k, v]) => (
              <div key={k} className="rounded-xl bg-slate-900/60 border border-slate-800 px-4 py-3"><div className="text-lg font-bold font-display">{v}</div><div className="dg-eyebrow">{k}</div></div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="dg-card rounded-xl p-5" data-testid="customer-sessions">
          <div className="dg-eyebrow flex items-center gap-1 mb-3"><CalendarDays className="w-3 h-3" /> Sessions</div>
          {sessions.length === 0 ? <div className="text-sm text-slate-500">No sessions yet.</div> : (
            <ul className="space-y-3">
              {sessions.map((b) => (
                <li key={b.booking_id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
                  <div className="flex flex-wrap justify-between gap-2"><span className="text-slate-200">{new Date(b.scheduled_at).toLocaleString()}</span><span className="dg-chip">{b.status.replace("_", " ")}</span></div>
                  <div className="text-slate-400 mt-1">{b.service_name} · {b.meeting_method}{b.payment_status === "paid" ? ` · paid $${b.deposit_usd}` : ""} · <span className="font-mono text-cyan-300">{b.booking_id}</span></div>
                  {b.notes && <div className="text-slate-500 italic mt-1">"{b.notes}"</div>}
                  {b.admin_notes && <div className="mt-2 text-xs text-amber-200/90 flex gap-1.5"><StickyNote className="w-3 h-3 mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{b.admin_notes}</span></div>}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="dg-card rounded-xl p-5" data-testid="customer-tickets">
          <div className="dg-eyebrow flex items-center gap-1 mb-3"><Ticket className="w-3 h-3" /> Ask Dr.Gadget tickets</div>
          {tix.length === 0 ? <div className="text-sm text-slate-500">No tickets yet.</div> : (
            <ul className="space-y-3">
              {tix.map((t) => (
                <li key={t.ticket_id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
                  <div className="flex flex-wrap justify-between gap-2"><span className="text-slate-200">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""} · {t.issue_type}</span><span className="dg-chip">{t.status}</span></div>
                  <div className="text-slate-400 mt-1 line-clamp-3">{t.description}</div>
                  <div className="font-mono text-xs text-cyan-300 mt-1">{t.ticket_id}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {notes.length > 0 && <p className="text-xs text-slate-500">{notes.length} private note{notes.length === 1 ? "" : "s"} on file — edit them from the Bookings tab.</p>}
    </div>
  );
}
